/**
 * Hook for document upload and extraction workflow.
 *
 * Provides state management for:
 * - File upload to extraction API
 * - Extraction status polling
 * - Extracted data retrieval
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  uploadDocument,
  triggerExtraction,
  getDocumentStatus,
  getExtractionData,
  listDocuments,
  pollForCompletion,
  DocumentStatus,
  DocumentMetadata,
  ExtractionDataResponse,
  ExtractionAPIError,
  getErrorInfo,
} from '@/lib/extraction-api';

export interface ExtractionJob {
  documentId: string;
  filename: string;
  status: DocumentStatus;
  documentType: string | null;
  confidence: string | null;
  error: string | null;
  data: Record<string, unknown> | null;
}

export interface UseDocumentExtractionOptions {
  clientId: string;
  orgId?: string;
  familyId?: string;
  onExtractionComplete?: (job: ExtractionJob) => void;
  onExtractionError?: (error: Error, job: ExtractionJob) => void;
  /** Maximum number of concurrent uploads (default: 3) */
  maxConcurrent?: number;
}

export function useDocumentExtraction(options: UseDocumentExtractionOptions) {
  const { clientId, orgId, familyId, onExtractionComplete, onExtractionError, maxConcurrent = 3 } = options;
  const queryClient = useQueryClient();

  // Track active extraction jobs
  const [activeJobs, setActiveJobs] = useState<Map<string, ExtractionJob>>(new Map());
  // Ref to avoid stale closures in mutation callbacks
  const activeJobsRef = useRef<Map<string, ExtractionJob>>(activeJobs);
  useEffect(() => {
    activeJobsRef.current = activeJobs;
  }, [activeJobs]);

  // Track batch upload progress
  const [batchProgress, setBatchProgress] = useState<{
    total: number;
    completed: number;
    inProgress: boolean;
  }>({ total: 0, completed: 0, inProgress: false });

  // Update a job's state
  const updateJob = useCallback((documentId: string, updates: Partial<ExtractionJob>) => {
    setActiveJobs(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(documentId);
      if (existing) {
        newMap.set(documentId, { ...existing, ...updates });
      }
      return newMap;
    });
  }, []);

  // Remove a job
  const removeJob = useCallback((documentId: string) => {
    setActiveJobs(prev => {
      const newMap = new Map(prev);
      newMap.delete(documentId);
      return newMap;
    });
  }, []);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const result = await uploadDocument(file, clientId, orgId, familyId);
      return { ...result, file };
    },
    onSuccess: (result) => {
      // Add job to tracking
      const job: ExtractionJob = {
        documentId: result.document_id,
        filename: result.filename,
        status: 'uploaded',
        documentType: null,
        confidence: null,
        error: null,
        data: null,
      };
      setActiveJobs(prev => new Map(prev).set(result.document_id, job));
      toast.success(`Uploaded ${result.filename}`);
    },
    onError: (error: Error) => {
      let message = error.message;
      if (error instanceof ExtractionAPIError) {
        const { message: friendlyMessage } = getErrorInfo(error);
        message = friendlyMessage;
      }
      toast.error('Upload failed', { description: message });
    },
  });

  // Extract mutation (with polling)
  const extractMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Trigger extraction
      await triggerExtraction(documentId);

      // Poll for completion
      const result = await pollForCompletion(
        documentId,
        (status, doc) => {
          updateJob(documentId, {
            status,
            documentType: doc.document_type,
            confidence: doc.classification_confidence,
          });
        },
        2000, // Poll every 2 seconds
        120000 // Timeout after 2 minutes
      );

      return result;
    },
    onSuccess: (result, documentId) => {
      const job = activeJobsRef.current.get(documentId);
      if (job) {
        const updatedJob: ExtractionJob = {
          ...job,
          status: result.status,
          documentType: result.document_type,
          data: result.data,
          error: result.error_message,
        };
        updateJob(documentId, updatedJob);

        if (result.status === 'completed') {
          toast.success(`Extracted ${job.filename}`, {
            description: `Type: ${result.document_type}`,
          });
          onExtractionComplete?.(updatedJob);
        } else if (result.status === 'failed') {
          toast.error(`Extraction failed for ${job.filename}`, {
            description: result.error_message || 'Unknown error',
          });
          onExtractionError?.(new Error(result.error_message || 'Extraction failed'), updatedJob);
        }
      }

      // Invalidate document list cache
      queryClient.invalidateQueries({ queryKey: ['extraction-documents'] });
    },
    onError: (error: Error, documentId) => {
      const job = activeJobsRef.current.get(documentId);
      let message = error.message;
      if (error instanceof ExtractionAPIError) {
        const { message: friendlyMessage } = getErrorInfo(error);
        message = friendlyMessage;
      }

      updateJob(documentId, {
        status: 'failed',
        error: message,
      });

      toast.error('Extraction failed', { description: message });

      if (job) {
        onExtractionError?.(error, { ...job, status: 'failed', error: message });
      }
    },
  });

  // Check if online before making requests
  const checkOnline = useCallback(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toast.error('No internet connection', {
        description: 'Please check your connection and try again.',
      });
      return false;
    }
    return true;
  }, []);

  // Combined upload and extract
  const uploadAndExtract = useCallback(async (file: File) => {
    // Check network status first
    if (!checkOnline()) {
      throw new Error('No internet connection');
    }

    // Upload
    const uploadResult = await uploadMutation.mutateAsync(file);

    // Extract
    await extractMutation.mutateAsync(uploadResult.document_id);

    return uploadResult.document_id;
  }, [uploadMutation, extractMutation, checkOnline]);

  // Batch upload and extract multiple files in parallel
  const uploadBatch = useCallback(async (files: File[]) => {
    if (!checkOnline()) {
      throw new Error('No internet connection');
    }

    if (files.length === 0) return [];

    // Initialize batch progress
    setBatchProgress({ total: files.length, completed: 0, inProgress: true });

    const results: string[] = [];

    // Process files in chunks for controlled parallelism
    const processFile = async (file: File): Promise<string | null> => {
      try {
        // Upload
        const uploadResult = await uploadMutation.mutateAsync(file);

        // Extract (don't wait for completion, just trigger)
        extractMutation.mutate(uploadResult.document_id);

        return uploadResult.document_id;
      } catch (error) {
        // Error already handled by mutation callbacks
        return null;
      } finally {
        setBatchProgress(prev => ({
          ...prev,
          completed: prev.completed + 1,
        }));
      }
    };

    // Process in batches of maxConcurrent
    for (let i = 0; i < files.length; i += maxConcurrent) {
      const batch = files.slice(i, i + maxConcurrent);
      const batchResults = await Promise.all(batch.map(processFile));
      results.push(...batchResults.filter((id): id is string => id !== null));
    }

    // Finish batch
    setBatchProgress(prev => ({ ...prev, inProgress: false }));

    // Invalidate document list
    queryClient.invalidateQueries({ queryKey: ['extraction-documents'] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });

    return results;
  }, [uploadMutation, extractMutation, checkOnline, maxConcurrent, queryClient]);

  // Retry extraction for a failed job
  const retryExtraction = useCallback(async (documentId: string) => {
    // Check network status first
    if (!checkOnline()) {
      return;
    }

    const job = activeJobsRef.current.get(documentId);
    if (!job) {
      toast.error('Job not found');
      return;
    }

    // Reset job status
    updateJob(documentId, {
      status: 'processing',
      error: null,
    });

    toast.info(`Retrying extraction for ${job.filename}...`);

    try {
      await extractMutation.mutateAsync(documentId);
    } catch (error) {
      // Error handling done in mutation callbacks
    }
  }, [updateJob, extractMutation, checkOnline]);

  // Query for document list
  const documentsQuery = useQuery({
    queryKey: ['extraction-documents', clientId],
    queryFn: () => listDocuments(clientId),
    enabled: !!clientId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Query for single document status
  const useDocumentStatus = (documentId: string | null) => {
    return useQuery({
      queryKey: ['extraction-document', documentId],
      queryFn: () => documentId ? getDocumentStatus(documentId) : null,
      enabled: !!documentId,
      refetchInterval: (data) => {
        // Poll while processing
        if (data?.document?.status === 'processing') {
          return 2000;
        }
        return false;
      },
    });
  };

  // Query for extraction data
  const useExtractionData = (documentId: string | null) => {
    return useQuery({
      queryKey: ['extraction-data', documentId],
      queryFn: () => documentId ? getExtractionData(documentId) : null,
      enabled: !!documentId,
    });
  };

  return {
    // State
    activeJobs: Array.from(activeJobs.values()),
    isUploading: uploadMutation.isPending,
    isExtracting: extractMutation.isPending,
    batchProgress,

    // Actions
    upload: uploadMutation.mutate,
    extract: extractMutation.mutate,
    uploadAndExtract,
    uploadBatch,
    retryExtraction,
    removeJob,

    // Queries
    documents: documentsQuery.data?.documents || [],
    documentsLoading: documentsQuery.isLoading,
    refetchDocuments: documentsQuery.refetch,

    // Hooks for individual documents
    useDocumentStatus,
    useExtractionData,
  };
}

export default useDocumentExtraction;
